<script setup>
  import { useOfferStore } from '../../stores/OfferStore';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import { computed, ref, onMounted, watch } from 'vue';
  import CartForm from './CartForm.vue';
  import Modal from '../ui/Modal.vue';
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n();

  const offerStore = useOfferStore();
  const cartStore = useBrowseCartStore();
  const showModal = ref(false);
  const moveType = ref('move');
  const selectedItem = ref('');
  const selectAllCheckbox = ref(null);
  const currentSortColumn = ref('');
  const sortDirection = ref('asc'); // 'asc' or 'desc'

  const refundMode = computed(() =>{
    return cartStore.refund_mode;
  });

  const toggleSelectAll = (event) => {
    offerStore.toggleSelectAll(event.target.checked, cartStore.refund_mode);
  };

  const policy = computed(() => {
    return cartStore.cart.policy;
  });

  const cartId = computed(() => {
    return cartStore.cart.id;
  });

  const offersSelected = computed(() => {
    return offerStore.offersSelected
  });

  const offersSelectedReplaced = computed(() => {
    return offerStore.offersSelectedReplaced
  });

  const deleteCheckedOffers = (id) => {
    const offerIds = offersSelected.value.map(offer => offer.offer.id);
    if(confirm(t('cart.header.confirm_delete'))) {
      const params = { id: id, offerIds: offerIds };
      offerStore.deleteCheckedOffersByCartId(params);
    }
  }

  const moveItemsVisible = computed(() => {
    if (offersSelected.value.length === 0) {
      return false;
    }

    return true;
  });

  const deleteItemsVisible = computed(() => {
    if (offersSelected.value.length === 0) {
      return false;
    }

    return true;
  });

  const orderBy = (column) => {
    offerStore.offersOrder(column);
    selectedItem.value = listSortBy.value[column];
    currentSortColumn.value = column;
  };

  const sortByColumn = (column) => {
    selectedItem.value = listSortBy.value[column];
    // Toggle direction if clicking the same column
    if (currentSortColumn.value === column) {
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
      currentSortColumn.value = column;
      sortDirection.value = 'asc';
    }

    offerStore.offersOrder(column);

    // reverse ordering
    if (sortDirection.value === 'desc') {
      offerStore.offers.reverse();
    }
  };

  const sortOrder = (column) => {
    if (currentSortColumn.value === column) {
      return sortDirection.value === 'asc' ? 'desc' : 'asc';
    }
    return 'asc';
  };

  const sortOrderLabel = (column) => {
    const direction = sortOrder(column);
    return direction === 'asc' ? t('cart.header.labels.ascending') : t('cart.header.labels.descending');
  };

  const getSortIcon = (column) => {
    if (currentSortColumn.value !== column) return '';
    return sortDirection.value === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill';
  };

  const filterOptions = computed(() => {
    return {
      none: t('cart.header.filter.none'),
      yes: t('cart.header.filter.yes'),
      no: t('cart.header.filter.no')
    }
  });

  const filterGroups = [
    { key: 'already_purchased', labelKey: 'cart.header.filter.already_purchased' },
    { key: 'in_another_cart', labelKey: 'cart.header.filter.in_another_cart' },
    { key: 'web_reader_compatible', labelKey: 'cart.header.filter.web_reader_compatible' }
  ];

  const setFilter = (group, value) => {
    offerStore.setFilter(group, value);
    if (selectAllCheckbox.value) { selectAllCheckbox.value.checked = false; }
  };

  // Summary filters also clear the selection (FBKS-3962)
  watch(() => offerStore.summary_filters.length, () => {
    if (selectAllCheckbox.value) { selectAllCheckbox.value.checked = false; }
  });

  const listSortBy = computed(() => {
    return {
      none: t('cart.header.sort.none'),
      title: t('cart.header.sort.title'),
      release_date: t('cart.header.sort.release_date'),
      author: t('cart.header.sort.author'),
      format: t('cart.header.sort.format'),
      category: t('cart.header.sort.category'),
      audience: t('cart.header.sort.audience'),
      publisher: t('cart.header.sort.publisher'),
      unit_price: t('cart.header.sort.unit_price'),
      quantity: t('cart.header.sort.quantity'),
      total_price: t('cart.header.sort.total_price')
    }
  });

  const initializeStickyHeader = () => {
    const stickyHeaderChecker = () => {
      const header = document.querySelector('.cart-sticky-hidden');
      const headerCartSticky = document.querySelector('.cart-sticky');
      const sticky = header.offsetTop;
      if (window.scrollY > sticky) {
        headerCartSticky.classList.add('sticky');
      } else {
        headerCartSticky.classList.remove('sticky');
      }
    };
    window.onscroll = stickyHeaderChecker;
  };

  const openMoveItemsModal = (type = 'move') => {
    showModal.value = true;
    moveType.value = type;
  };

  const visibleCheckAll = computed(() => {
    return cartStore.cart.policy.cart_editable || refundMode.value;
  });

  onMounted(() => {
    initializeStickyHeader();
    selectedItem.value = listSortBy.value['none'];
  });
</script>

<template>
  <div class="cart-sticky-hidden"></div>
  <div class="full-screen cart-sticky">
    <div class="row mt-3 toolbar-container">
      <div class="col-auto d-flex align-items-center gap-2" v-if="policy.cart_editable && !refundMode && (moveItemsVisible || deleteItemsVisible)">
        <div class="btn-group button-bar-height" role="group" :aria-label="t('cart.actions.title')">
          <button
            type="button"
            data-bs-toggle="tooltip" :data-bs-title="t('cart.header.move_items_to_cart')"
            @click.prevent="openMoveItemsModal('move')"
            class="btn btn-primary"
            v-if="moveItemsVisible">
            <i class="bi bi-arrow-up-right-circle" aria-hidden="true"></i>
            <span class="sr-only">{{ t('cart.header.move_items_to_cart') }}</span>
          </button>
          <button
            type="button"
            data-bs-toggle="tooltip" :data-bs-title="t('cart.header.copy_items_to_cart')"
            @click.prevent="openMoveItemsModal('copy')"
            class="btn btn-primary"
            v-if="moveItemsVisible">
            <i class="bi bi-copy" aria-hidden="true"></i>
            <span class="sr-only">{{ t('cart.header.copy_items_to_cart') }}</span>
          </button>
          <button
            type="button"
            data-bs-toggle="tooltip" :data-bs-title="t('cart.header.delete')"
            @click.prevent="deleteCheckedOffers(cartId)"
            class="btn btn-primary"
            v-if="deleteItemsVisible">
            <i class="bi bi-trash3" aria-hidden="true"></i>
            <span class="sr-only">{{ t('cart.header.delete') }}</span>
          </button>
        </div>
        <span class="selected-count text-muted text-nowrap" aria-live="polite">
          {{ t('cart.header.selected_count', offersSelected.length) }}
        </span>
      </div>
      <div class="col">
        <div class="btn-group" role="group" :aria-label="t('cart.header.filter_and_sort_group')">
          <div class="btn-group" v-for="group in filterGroups" :key="group.key">
            <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" aria-haspopup="listbox">
              {{ t(group.labelKey) }}: {{ filterOptions[offerStore.filters[group.key]] }}
            </button>
            <ul class="dropdown-menu" role="listbox">
              <li v-for="(value, key) in filterOptions" :key="key" role="option" :aria-selected="offerStore.filters[group.key] === key">
                <a class="dropdown-item" href="#" @click.prevent="setFilter(group.key, key)">{{ value }}</a>
              </li>
            </ul>
          </div>
          <div class="btn-group">
            <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" aria-haspopup="listbox">
              {{ t('cart.header.sort_by_query') }}: {{ selectedItem }}
            </button>
            <ul class="dropdown-menu" role="listbox">
              <li v-for="(value, key) in listSortBy" :key="key" :value="key" role="option" :tabindex="currentSortColumn === key ? 0 : -1" :aria-selected="currentSortColumn === key"><a class="dropdown-item" href="#" @click.prevent="orderBy(key)">{{ t(`cart.header.sort.${key}`) }}</a></li>
            </ul>
          </div>
          <button
            type="button"
            class="btn btn-outline-primary text-nowrap"
            v-if="offerStore.filters.publisher"
            :aria-label="t('cart.header.filter.clear_publisher')"
            data-bs-toggle="tooltip" :data-bs-title="t('cart.header.filter.clear_publisher')"
            @click="setFilter('publisher', null)">
            {{ t('cart.header.labels.publisher') }}: {{ offerStore.filters.publisher.name }}
            <i class="bi bi-x-lg ms-1" aria-hidden="true"></i>
          </button>
        </div>
      </div>
      <div class="col-auto ms-auto" id="cart-toolbar-actions" v-if="cartStore.cart.cart_content_summary"></div>
    </div>

    <div class="cart-grid cart-grid--header" role="group" aria-labelledby="cart-name">
      <div class="form-check cart-grid__column cart-grid__checkbox-content ms-3" v-if="visibleCheckAll">
        <input class="form-check-input" type="checkbox" value="" id="cart-items-select-all" ref="selectAllCheckbox" @change="toggleSelectAll">
      </div>
      <label class="form-check-label cart-grid__column" for="cart-items-select-all">
        {{ t('cart.header.labels.select_all_deselect_all') }}
      </label>
      <button
        id="header-release-date"
        type="button"
        class="btn cart-grid__column cart-header--center-text cart-header--sortable"
        @click="sortByColumn('release_date')"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.sort.release_date')">
        {{ t('cart.header.labels.release_date') }}
        <span class="sr-only">{{ sortOrderLabel('release_date') }}</span>
        <i class="bi" :class="getSortIcon('release_date')" v-if="currentSortColumn === 'release_date'" aria-hidden="true"></i>
      </button>
      <button
        id="header-category"
        type="button"
        class="btn cart-grid__column cart-header--center-text cart-header--sortable"
        @click="sortByColumn('category')"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.sort.category')">
        {{ t('cart.header.labels.category') }}
        <span class="sr-only">{{ sortOrderLabel('category') }}</span>
        <i class="bi" :class="getSortIcon('category')" v-if="currentSortColumn === 'category'" aria-hidden="true"></i>
      </button>
      <button
        id="header-audience"
        type="button"
        class="btn cart-grid__column cart-header--center-text cart-header--sortable"
        @click="sortByColumn('audience')"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.sort.audience')">
        {{ t('cart.header.labels.audience') }}
        <span class="sr-only">{{ sortOrderLabel('audience') }}</span>
        <i class="bi" :class="getSortIcon('audience')" v-if="currentSortColumn === 'audience'" aria-hidden="true"></i>
      </button>
      <button
        id="header-publisher"
        type="button"
        class="btn cart-grid__column cart-header--center-text cart-header--sortable"
        @click="sortByColumn('publisher')"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.sort.publisher')">
        {{ t('cart.header.labels.publisher') }}
        <span class="sr-only">{{ sortOrderLabel('publisher') }}</span>
        <i class="bi" :class="getSortIcon('publisher')" v-if="currentSortColumn === 'publisher'" aria-hidden="true"></i>
      </button>
      <!-- FBKS-3907: hidden for now
      <span class="cart-grid__column cart-header--center-text">{{ t('cart.header.labels.online_reading_listening') }}</span>
      -->
      <span id="header-status" class="cart-grid__column cart-header--center-text">{{ t('cart.header.labels.status') }}</span>
      <button
        id="header-unit-price"
        type="button"
        class="btn cart-grid__column cart-header--center-text cart-header--sortable"
        @click="sortByColumn('unit_price')"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.sort.unit_price')">
        {{ t('cart.header.labels.price') }}
        <span class="sr-only">{{ sortOrderLabel('unit_price') }}</span>
        <i class="bi" :class="getSortIcon('unit_price')" v-if="currentSortColumn === 'unit_price'" aria-hidden="true"></i>
      </button>
      <button
        id="header-quantity"
        type="button"
        class="btn cart-grid__column cart-header--center-text cart-header--sortable"
        @click="sortByColumn('quantity')"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.sort.quantity')">
        {{ t('cart.header.labels.quantity') }}
        <span class="sr-only">{{ sortOrderLabel('quantity') }}</span>
        <i class="bi" :class="getSortIcon('quantity')" v-if="currentSortColumn === 'quantity'" aria-hidden="true"></i>
      </button>
      <button
        id="header-total-price"
        type="button"
        class="btn cart-grid__column cart-header--center-text cart-header--sortable"
        @click="sortByColumn('total_price')"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.sort.total_price')">
        {{ t('cart.header.labels.total_price') }}
        <span class="sr-only">{{ sortOrderLabel('total_price') }}</span>
        <i class="bi" :class="getSortIcon('total_price')" v-if="currentSortColumn === 'total_price'" aria-hidden="true"></i>
      </button>
    </div>
    <Modal :show="showModal" @close="showModal = false">
      <template v-slot:title>
        {{ moveType === 'copy' ? t('cart.header.select_destination_cart_copy') : t('cart.header.select_destination_cart_move') }}
      </template>
      <CartForm @close="showModal = false" :cartId="cartId" :moveType="moveType" />
    </Modal>
  </div>
</template>

<style scoped>
  a.disabled {
    pointer-events: none;
    cursor: default;
  }
  .cart-sort-box {
    padding-bottom: 2rem;
  }
  .sticky {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 10;

    & + .sticky-spacer {
      padding-top: 4rem;
    }
  }
  .cart-header--sortable {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
  }
  .cart-header--sortable:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  .cart-header--sortable i {
    margin-left: 0.25rem;
    font-size: 0.75rem;
  }
  .button-bar-height {
    height: 38px;
  }
  .toolbar-container {
    padding: 0 var(--side-margin);
  }
  /* FBKS-3907: pin header cells to their named grid areas so hiding the
     online_reader column doesn't shift the following headers out of place */
  #header-release-date { grid-area: release_date; }
  #header-category { grid-area: category; }
  #header-audience { grid-area: audience; }
  #header-publisher { grid-area: publisher; }
  #header-status { grid-area: states; }
  #header-unit-price { grid-area: price; }
  #header-quantity { grid-area: quantity; }
  #header-total-price { grid-area: total_price; }
</style>
